// this module is a place to centralize all code for building urls to legacy provet cloud

import type { DigitalPracticeChatWidgetUrlParams } from '@/models/digitalPractice.js'
import type { TemplateContext } from '@/models/templateContext.js'
import { ClientCustomerType } from '@/models/client.js'

/**
 * builds a url pointing to the legacy provet cloud app, handling cases
 * where the legacyBaseUrl is set to '/'.
 *
 * Uses `legacyBaseUrl` from runtime config, which resolves to:
 * - `REVIEW_APP_URL` if set (for review app development)
 * - `apiBaseUrl` otherwise (backwards compatible)
 *
 * @param {Object} params - configuration object
 * @param {string} [params.provetId] - optional provet instance identifier. omit when the path already contains it
 * @param {string} params.path - the endpoint path to append to the base url
 * @returns {URL} a URL object pointing to the legacy provet cloud application
 *
 * @example
 * // with provetId
 * buildLegacyProvetUrl({ provetId: '33', path: '/client/123/tabs/' })
 *
 * @example
 * // without provetId (when path already contains it from API response)
 * buildLegacyProvetUrl({ path: '4/laboratory/referrals/external_attachment/123/iframe_modal/' })
 */
export function buildLegacyProvetUrl(params: {
  provetId?: string
  path: string
}) {
  const config = useRuntimeConfig()
  const normalizedPath = params.path.startsWith('/')
    ? params.path.substring(1)
    : params.path
  const base =
    config.public.legacyBaseUrl === '/'
      ? location.origin
      : config.public.legacyBaseUrl

  const provetId = params.provetId
    ? config.public.remoteProvetId || params.provetId
    : undefined
  const pathname = provetId
    ? `/${provetId}/${normalizedPath}`
    : `/${normalizedPath}`

  return new URL(pathname, base)
}

/**
 * Transforms a URL (from API response) to use legacyBaseUrl.
 * Handles both absolute URLs and relative paths.
 *
 * Use this for URLs received from the backend (e.g., navigation sidebar target_url)
 * that need to point to the legacy app instead of the current origin.
 */
/** Provet legacy paths: /{id}/... or /next/{id}/... */
const legacyProvetPathPattern = /^\/(?:next\/)?\d+\//

function isLocalDjangoDevOrigin(origin: string): boolean {
  try {
    const u = new URL(origin)
    return (
      (u.hostname === 'localhost' || u.hostname === '127.0.0.1') &&
      u.port === '8000'
    )
  } catch {
    return false
  }
}

export function getLegacyUrl(targetUrl: string): string {
  const config = useRuntimeConfig()

  try {
    const url = new URL(targetUrl)
    const apiOrigin = config.public.apiBaseUrl?.replace(/\/$/, '')
    const legacyBaseRaw = config.public.legacyBaseUrl ?? '/'
    const legacyOrigin = legacyBaseRaw.replace(/\/$/, '')
    const isRemoteLegacyBase =
      legacyBaseRaw.startsWith('http://') ||
      legacyBaseRaw.startsWith('https://')

    // If URL points to apiBaseUrl origin and legacyBaseUrl is different, transform it
    if (url.origin === apiOrigin && apiOrigin !== legacyOrigin) {
      return new URL(
        url.pathname + url.search + url.hash,
        config.public.legacyBaseUrl,
      ).toString()
    }

    // dev:fe-login sets apiBaseUrl to http://localhost:5171 (proxy), but the API still
    // returns absolute legacy links with http://localhost:8000. Rewrite those to
    // legacyBaseUrl when targeting a remote backend (REVIEW_APP_URL).
    if (
      isRemoteLegacyBase &&
      url.origin !== legacyOrigin &&
      legacyProvetPathPattern.test(url.pathname) &&
      isLocalDjangoDevOrigin(url.origin)
    ) {
      return new URL(
        url.pathname + url.search + url.hash,
        legacyBaseRaw,
      ).toString()
    }

    // Already correct origin or external URL
    return targetUrl
  } catch {
    // Relative path - make absolute with legacyBaseUrl
    const base =
      config.public.legacyBaseUrl === '/'
        ? location.origin
        : config.public.legacyBaseUrl

    return new URL(targetUrl, base).toString()
  }
}

export function patientLocationsDashboardUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: '/organization/patient_locations/dashboard/',
  })
}

export function patientLocationPrintUrl(params: {
  provetId: string
  departmentId?: string
  patientName?: string
  location: string[]
  statuses: string[]
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: '/organization/patient_locations/dashboard/pdf/',
  })

  if (params.departmentId) {
    url.searchParams.set('department_id', params.departmentId)
  }

  if (params.patientName) {
    url.searchParams.set(
      'locations_filter_form-patient_search',
      params.patientName,
    )
  }

  // locations
  params.location.forEach((location) => {
    // we only want the first ID in a comma-separated list, which is the parent ID
    const [id] = location.split(',')
    url.searchParams.append('locations_filter_form-locations[]', id)
  })

  // statuses
  params.statuses.forEach((status) => {
    url.searchParams.append('locations_filter_form-statuses[]', status)
  })

  return url
}

export function clientDetailsUrl(params: {
  provetId: string
  clientId: string
  legacyUrl: true
}): URL
export function clientDetailsUrl(params: {
  provetId: string
  clientId: string
  legacyUrl: false
}): string
export function clientDetailsUrl(params: {
  provetId: string
  clientId: string
  legacyUrl: boolean
}): URL | string
export function clientDetailsUrl(params: {
  provetId: string
  clientId: string
  legacyUrl: boolean
}) {
  return params.legacyUrl
    ? buildLegacyProvetUrl({
        provetId: params.provetId,
        path: `/client/${params.clientId}/tabs/`,
      })
    : `/${params.provetId}/clients/${params.clientId}`
}

export function clientCompanyEditUrl<T extends boolean>(params: {
  provetId: string
  clientId: string
  customerType: string | null | undefined
  legacyUrl: T
}): T extends true ? URL : string {
  if (params.legacyUrl) {
    return buildLegacyProvetUrl({
      provetId: params.provetId,
      path: `/client/${params.clientId}/edit/`,
    }) as T extends true ? URL : string
  }
  const isPrivate =
    !params.customerType || params.customerType === ClientCustomerType.Private

  return (
    isPrivate
      ? `/${params.provetId}/clients/${params.clientId}/edit`
      : `/${params.provetId}/organization/${params.clientId}/edit`
  ) as T extends true ? URL : string
}

// Similar to clientDetailsUrl but omits the /overview from the URL
export function clientPatientTabsUrl(params: {
  provetId: string
  clientId: string
  legacyUrl: boolean
}) {
  return params.legacyUrl
    ? buildLegacyProvetUrl({
        provetId: params.provetId,
        path: `/client/${params.clientId}/tabs/`,
      })
    : `/${params.provetId}/clients/${params.clientId}`
}

export function patientLocationAddUrl(params: { provetId: string }) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: '/organization/administration/department/#patient_locations',
  })
}

/**
 * Builds the legacy Provet Cloud search URL for clients and patients.
 *
 * @param params - URL parameters.
 * @param params.provetId - The Provet instance identifier.
 * @returns URL object pointing to the legacy search page.
 */
export function clientAndPatientSearchUrl(params: { provetId: string }) {
  return buildLegacyProvetUrl({ provetId: params.provetId, path: '/search' })
}

export function patientDetailsUrl(params: {
  provetId: string
  patientId: string
  clientId: string
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/client/${params.clientId}/tabs/`,
  })
  url.searchParams.set('patient_id', params.patientId)

  return url
}

export function patientEditUrl<T extends boolean>(params: {
  provetId: string
  patientId: string
  legacyUrl: T
}): T extends true ? URL : string {
  if (params.legacyUrl) {
    return buildLegacyProvetUrl({
      provetId: params.provetId,
      path: `/patient/${params.patientId}/edit/`,
    }) as T extends true ? URL : string
  }
  return `/${params.provetId}/patients/${params.patientId}/edit` as T extends true
    ? URL
    : string
}

export function patientHistoryUrl(params: {
  provetId: string
  patientId: string
  clientId: string
}) {
  const url = patientDetailsUrl(params)
  url.searchParams.set('tab', 'history_tab')
  return url
}

export function patientUnmergeUrl(params: {
  provetId: string
  oldPatientId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/patient/${params.oldPatientId}/unmerge/`,
  })
}

export function consultationDetailsUrl(params: {
  provetId: string
  consultationId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/consultation/${params.consultationId}`,
  })
}

export function consultationAdmitUrl(params: {
  provetId: string
  consultationId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/consultation/${params.consultationId}/admit/`,
  })
}

/**
 * Builds a URL for the appointment admit page in the legacy Provet application.
 *
 * @param params - The parameters for building the URL.
 * @param params.provetId - The unique identifier of the Provet instance.
 * @param params.appointmentId - The unique identifier of the appointment to admit.
 * @param params.referrer - Optional URL or string to set as the referrer query parameter,
 *   allowing the admit page to redirect back after completion.
 * @returns A URL object pointing to the appointment admit page.
 */
export function appointmentAdmitUrl(params: {
  provetId: string
  appointmentId: string
  referrer?: URL | string
}): URL {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/appointment/${params.appointmentId}/admit/`,
  })

  if (params.referrer) {
    url.searchParams.set('referrer', params.referrer.toString())
  }

  return url
}

export const appointmentOverviewUrl = (params: {
  provetId: string
  appointmentId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `personnel/calendar/appointments/${params.appointmentId}/nfe_overview`,
  })

export function consultationVersionSwitchUrl(params: {
  provetId: string
  consultationId: string
  version: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/consultation/${params.consultationId}/version/${params.version}/set`,
  })
}

export function consultationTextTemplateUrl(params: {
  provetId: string
  consultationId: string
  clientId: string
  templateTypes: string[]
}) {
  if (!params.templateTypes || params.templateTypes.length === 0) {
    throw new Error('templateTypes must be provided and cannot be empty')
  }

  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_text_templates/target/all?consultation=${params.consultationId}&client=${params.clientId}&identifier=consultation-notes-single&template_types=${params.templateTypes.map(encodeURIComponent).join(',')}`,
  })
}

export const consultationNoteVersionHistoryUrl = (params: {
  provetId: string
  consultationId: string
  patientId: string
  noteId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/patient/${params.patientId}/note/${params.noteId}/nfe_consultation_note/versions/`,
  })

export function consultationDischargeInstructionsVersionHistoryUrl(params: {
  provetId: string
  consultationId: string
  patientId?: string
}) {
  const urlExtension = params.patientId
    ? `consultation/${params.consultationId}/patient/${params.patientId}/nfe_dischargeinstruction/versions/`
    : `consultation/${params.consultationId}/nfe_dischargeinstruction/versions/`

  return buildLegacyProvetUrl({ provetId: params.provetId, path: urlExtension })
}

export const consultationDischargeInstructionsEmailUrl = (params: {
  provetId: string
  consultationId: string
  patientId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/patient/${params.patientId}/nfe_dischargeinstruction/mail/`,
  })

export const consultationDiagnosisCreateUrl = (params: {
  provetId: string
  consultationId: string
  listItemCode: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_diagnosis/create/${encodeURIComponent(params.listItemCode)}/`,
  })

export const consultationDiagnosisEditUrl = (params: {
  provetId: string
  consultationId: string
  diagnosisId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_diagnosis/${encodeURIComponent(params.diagnosisId)}/`,
  })

export const consultationDiagnosisSearchTreeUrl = (params: {
  provetId: string
  consultationId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_diagnosis_tree/${params.consultationId}/`,
  })

/**
 * Endpoint to create a new PDF assignment form, returns url to the form
 */
export const pdfAssignmentCreateFormUrl = (params: {
  provetId: string
  templateInstanceId?: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `organization/forms/nfe_pdf_assignment/consultation/${params.templateInstanceId || ''}`,
  })

export const consultationLaboratoryAddUrl = (params: {
  provetId: string
  consultationId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_consultation_laboratory/create/`,
  })

export const consultationLaboratoryEditUrl = (params: {
  provetId: string
  consultationId: string
  analysisId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_laboratory/${params.analysisId}/`,
  })

export const provetModalContainerUrl = (params: {
  provetId: string
  url: URL
}): URL => {
  const containerUrl = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: 'lazy_loader/modal/',
  })

  // Use searchParams API to properly encode the content_url parameter
  // This ensures ampersands in the nested URL are correctly encoded as %26
  containerUrl.searchParams.set('content_url', params.url.toString())

  return containerUrl
}

export const treatmentBundleSelectItemsUrl = (params: {
  provetId: string
  consultationId: string
  templateId: string
}) => {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_items_template/${params.templateId}/`,
  })
}

export const treatmentProcedureCreateUrl = (params: {
  provetId: string
  consultationId: string
  orgItemId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_procedure/org_item/${params.orgItemId}/`,
  })

export const treatmentProcedureEditUrl = (params: {
  provetId: string
  consultationId: string
  itemId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_procedure/${params.itemId}/`,
  })

export const treatmentSupplyCreateUrl = (params: {
  provetId: string
  consultationId: string
  orgItemId: string
  prescriptionRepeatId?: string
}) => {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_supply/org_item/${params.orgItemId}/`,
  })
  if (params.prescriptionRepeatId) {
    url.searchParams.set('prescription_repeat', params.prescriptionRepeatId)
  }
  return url
}

export const treatmentSupplyEditUrl = (params: {
  provetId: string
  consultationId: string
  itemId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_supply/${params.itemId}/`,
  })

export const treatmentFoodCreateUrl = (params: {
  provetId: string
  consultationId: string
  orgItemId: string
  prescriptionRepeatId?: string
}) => {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_food/org_item/${params.orgItemId}/`,
  })
  if (params.prescriptionRepeatId) {
    url.searchParams.set('prescription_repeat', params.prescriptionRepeatId)
  }
  return url
}

export const treatmentFoodEditUrl = (params: {
  provetId: string
  consultationId: string
  itemId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_food/${params.itemId}/`,
  })

export const treatmentMedicineCreateUrl = (params: {
  provetId: string
  consultationId: string
  orgItemId: string
  prescriptionRepeatId?: string
}) => {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_medicine/org_item/${params.orgItemId}/`,
  })
  if (params.prescriptionRepeatId) {
    url.searchParams.set('prescription_repeat', params.prescriptionRepeatId)
  }
  return url
}

export const treatmentMedicineEditUrl = (params: {
  provetId: string
  consultationId: string
  itemId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_medicine/${params.itemId}/`,
  })

export const refillAddUrl = (params: {
  provetId: string
  orgItemId: string
  patientId: string
  consultationId?: string
  prescriptionRepeatId?: string
}) => {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_refill/org_item/${params.orgItemId}/patient/${params.patientId}`,
  })
  if (params.consultationId) {
    url.searchParams.set('consultation_id', params.consultationId)
  }
  if (params.prescriptionRepeatId) {
    url.searchParams.set('prescription_repeat', params.prescriptionRepeatId)
  }
  return url
}

export const generalPrescriptionCreateUrl = (params: {
  provetId: string
  consultationId: string
  prescriptionType: '1' | '2' // '1' for written, '2' for phone
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `general_presc/consultation/${params.consultationId}/nfe_create/${params.prescriptionType}/`,
  })

export const generalPrescriptionEditUrl = (params: {
  provetId: string
  prescriptionId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `general_presc/${params.prescriptionId}/nfe_update/`,
  })

export const estimateItemsToConsultationUrl = (params: {
  provetId: string
  estimateId: string
}) =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `estimate/id/${params.estimateId}/nfe_items_to_consultation/`,
  })

export function loginUrl(params: { provetId: string; nextUrl?: URL }) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: '/auth/login',
  })

  if (params.nextUrl) {
    url.searchParams.set('next_url', params.nextUrl.toString())
  }

  return url
}

/**
 * Builds the URL for the new frontend login page.
 * @returns A URL object pointing to the new frontend login page.
 */
export function newLoginUrl(params: { provetId: string; nextUrl?: URL }): URL {
  const url = new URL(`/${params.provetId}/auth/login`, window.location.origin)

  if (params.nextUrl) {
    url.searchParams.set('next_url', params.nextUrl.toString())
  }

  return url
}

/**
 * Builds the URL for the forgot password page (legacy Django).
 */
export function forgotPasswordUrl(params: {
  provetId: string
  reason?: 'expired' | 'pwned' | 'reset'
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/auth/forgot_password/${params.reason ?? 'reset'}/`,
  })
}

/**
 * Sanitizes user-controlled `next_url` query param to prevent open redirects.
 * Allows only relative paths (e.g. `/4/dashboard`). Returns null for invalid or unsafe values.
 */
export function sanitizeNextUrl(raw: string | undefined): string | null {
  if (raw === undefined || raw === null || typeof raw !== 'string') {
    return null
  }
  const trimmed = raw.trim()
  // Allow only relative paths: must start with single `/`, not `//` (protocol-relative)
  if (trimmed.startsWith('//') || !trimmed.startsWith('/')) {
    return null
  }
  return trimmed
}

/**
 * Builds the URL for SSO/SAML login (legacy Django).
 */
export function ssoLoginUrl(params: {
  provetId: string
  ssoConfigurationId: string
  nextUrl?: string
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/auth/saml/${params.ssoConfigurationId}/login/`,
  })
  if (params.nextUrl) {
    url.searchParams.set('next_url', params.nextUrl)
  }
  return url
}

/**
 * Builds the URL for passwordless WebAuthn login (legacy Django).
 */
export function passwordlessLoginUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: '/auth/login/passwordless/',
  })
}

export function dashboardUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'dashboard/' })
}

export function userProfileUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'auth/user_profile/' })
}

export function userCommunicationsUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'auth/communications/' })
}

export function userTasksUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'auth/communications/#tasks' })
}

export const taskCreateUrl = (params: {
  provetId: string
  contentTypeId: string
  objectId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `task/nfe/${params.contentTypeId}/${params.objectId}/create/`,
  })

export const taskEditUrl = (params: {
  provetId: string
  taskId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `task/nfe/${params.taskId}/update/`,
  })

export const taskPreviewUrl = (params: {
  provetId: string
  taskId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `task/nfe/${params.taskId}/preview/`,
  })

export function knowledgeBaseUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'knowledge_base/' })
}

/* Report URLs ==================================================================================== */

export function userMyStatisticsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: 'reports/powerbi/?reportType=user',
  })
}

/* Financial Reports */
export function reportInvoicesUrl(params: {
  provetId: string
  legacyUrl: true
}): ReturnType<typeof buildLegacyProvetUrl>
export function reportInvoicesUrl(params: {
  provetId: string
  legacyUrl: false
}): string
export function reportInvoicesUrl(params: {
  provetId: string
  legacyUrl: boolean
}) {
  return params.legacyUrl
    ? buildLegacyProvetUrl({
        provetId: params.provetId,
        path: `reports/invoice/html/`,
      })
    : `/${params.provetId}/reports/invoices/`
}

export const reportPaymentUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/payment/html/' })

export const reportPrepaymentUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/prepayment/html/' })

export const reportSalesByPaymentUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/grouped_invoice/html/' })

export const reportItemPurchasesUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/item_purchases/html/' })

export const reportItemSalesUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/item_sales/html/' })

export const reportItemPriceChangeUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/item_price_change/html/' })

export const reportDiscountsUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/discounts/html/' })

export const reportDiscountInvoicesUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/discount_invoices/html/' })

export const reportCommissionsUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/commissions/html/' })

export const reportGrossProfitUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/gross_profit/html/' })

export const reportDebtorsAndCreditorsUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/debtors/html/' })

export const reportUnpaidInvoicesUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/unpaid_invoices/html/' })

export const reportBankPaymentProcessingUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'receivables/upload-file/' })

export const reportMonthEndUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/month_end/html/' })

export const reportOnlinePrescriptionFeesUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/epresc_fees/html/' })

export const reportCentralLaboratoryReferralRowsUrl = (provetId: string) =>
  buildLegacyProvetUrl({
    provetId,
    path: 'reports/central_lab_referrals/html/',
  })

export const reportCentralLaboratoryMonthEndUrl = (provetId: string) =>
  buildLegacyProvetUrl({
    provetId,
    path: 'reports/central_lab_month_end/html/',
  })

export const reportRegisterPaymentsUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/payment_registration/' })

export const reportGeneralLedgerUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/general_ledger/html/' })

export const reportElectronicJournalUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'fiscalsystem/electric_journal/' })

/* Practice Performance Reports */

export const reportConsultationCountUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/consultation/html/' })

export const reportTreatedSpeciesUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/consultation_species/html/' })

export const reportAppointmentsUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/appointment/html/' })

export const reportConsultationTimeUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/consultation_time/html/' })

export const reportDiagnosisUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/diagnosis/html/' })

export const reportBereavementUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/bereavement/html/' })

export const reportNewClientsUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/new_clients/html/' })

export const reportVaccinationUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/vaccination/html/' })

/* Stock Management Reports */

export const reportStockLevelUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/stock_report/html/' })

export const reportStockItemUsageUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/stock_item_usage/html/' })

export const reportMedicineUsageUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/medicine_usage/html/' })

export const reportInventoryUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/inventory/html/' })

export const reportCabinetDiscrepancyUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/cabinet_discrepancy/html/' })

/* Communication Reports */

export const reportFeedbackUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/feedback/html/' })

export const reportSentReferralsUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/sent_referrals/html/' })

export const reportSentSmsMessagesUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/sent_sms/html/' })

/* Lists Reports */

export const reportItemPriceListUrl = (provetId: string) =>
  buildLegacyProvetUrl({ provetId, path: 'reports/price_list/html/' })

/* Business Intelligence Reports */

export const reportBusinessIntelligenceUrl = (provetId: string) =>
  buildLegacyProvetUrl({
    provetId,
    path: 'reports/powerbi/?reportType=general',
  })

/** end of Report URLs ============================================================================ */

export function userProductIdeaUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'auth/aha/' })
}

export function userSupportHistoryUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'auth/zendesk/' })
}

export function userQuickLoginUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'auth/quick_switch_logout/' })
}

export function userSignOutUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'auth/logout/' })
}

export function releaseInformationUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'about/releases/' })
}

export function whatsNewUrl() {
  return `https://www.provet.cloud/release-log/`
}

export function newClientUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: '/client/new/' })
}

export function newAppointmentUrl(provetId: string, clientId?: string) {
  const url = buildLegacyProvetUrl({
    provetId,
    path: '/personnel/appointments/',
  })
  if (clientId) {
    url.searchParams.set('client_id', clientId)
  }

  return url
}

export function newCounterSaleUrl(provetId: string) {
  // the trailing slash is required!
  return buildLegacyProvetUrl({ provetId, path: '/countersale/new/' })
}

export function patientUrl(provetId: string, clientId: string, tab?: string) {
  const tabParam = tab ? `tabs/?tab=${tab}` : ''
  return buildLegacyProvetUrl({
    provetId,
    path: `/client/${clientId}/${tabParam}`,
  })
}
export function newPatientUrl(provetId: string, clientId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: `/client/${clientId}/newpatient/`,
  })
}
export function inventoryStockUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: '/stock/inventory/stock/' })
}

export function buildHealthPlanUrl(provetId: string, path: string) {
  return buildLegacyProvetUrl({ provetId, path: `/health_plans/${path}/` })
}

export function healthPlanDetailsUrl(provetId: string, healthPlanId: string) {
  return buildHealthPlanUrl(provetId, `health_plan/${healthPlanId}`)
}

export function newHealthPlanSubscriptionUrl(params: {
  provetId: string
  clientId?: string
  patientId?: string
}) {
  const url = buildHealthPlanUrl(params.provetId, 'subscription/new')
  if (params.clientId) {
    url.searchParams.set('client_id', params.clientId)
  }
  if (params.patientId) {
    url.searchParams.set('patient_id', params.patientId)
  }

  return url
}

export function healthPlanUseModalUrl(params: {
  provetId: string
  consultationId: string
  subscriptionId: string
}): URL {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `health_plans/consultation/${params.consultationId}/nfe_subscription/${params.subscriptionId}/use_items/`,
  })
}

export function generalSettingsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: 'organization/administration/organization/',
  })
}

export function templatesSettingsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: '/organization/administration/templates/',
  })
}

export function reminderTemplatesUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: '/organization/administration/reminder_templates/',
  })
}

export function departmentSettingsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: '/organization/administration/department/',
  })
}

export function clinicLocationOnlineBookingUrl(provetId: string) {
  return `/${provetId}/organization/clinic-location/online-booking`
}

export function catalogItemSubgroupsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: 'organization/administration/catalog/item_subgroups/',
  })
}

export function catalogItemListsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: 'organization/administration/catalog/item_lists/',
  })
}

export function catalogLaboratoryGroupsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: 'organization/administration/catalog/laboratory_groups/',
  })
}

export function catalogItemDiagnosesUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: 'organization/administration/catalog/item_diagnoses/',
  })
}

export function catalogItemsUrl(params: {
  provetId: string
  itemCategoryId?: string
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: 'organization/administration/catalog/items/',
  })

  if (params.itemCategoryId) {
    url.searchParams.set('item_category', params.itemCategoryId)
  }

  return url
}

export function getClientsAndPatientsTabsUrl(params: {
  provetId: string
  clientId: string
  tab: string
  patientId?: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/client/${params.clientId}/tabs/?tab=${params.tab}${params.patientId ? `&patient_id=${params.patientId}` : ''}`,
  })
}

export const clientBillingUrl = (provetId: string, clientId: string) => {
  if (!clientId) {
    return ''
  }

  const clientBillingUrl = clientDetailsUrl({
    provetId,
    clientId: clientId,
    legacyUrl: true,
  }) as URL
  clientBillingUrl.searchParams.set('tab', 'billing')

  return clientBillingUrl
}

export function invoicesUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'billing/invoice/' })
}

export function invoiceDetailsUrl(params: {
  provetId: string
  invoiceId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `billing/invoice/${params.invoiceId}`,
  })
}

export function invoiceFinishedDetailsUrl(params: {
  provetId: string
  invoiceId: string
  legacyUrl: true
}): URL
export function invoiceFinishedDetailsUrl(params: {
  provetId: string
  invoiceId: string
  legacyUrl: false
}): string
export function invoiceFinishedDetailsUrl(params: {
  provetId: string
  invoiceId: string
  legacyUrl: boolean
}): URL | string
export function invoiceFinishedDetailsUrl(params: {
  provetId: string
  invoiceId: string
  legacyUrl: boolean
}) {
  return params.legacyUrl
    ? buildLegacyProvetUrl({
        provetId: params.provetId,
        path: `billing/invoice_finished/${params.invoiceId}`,
      })
    : `/${params.provetId}/billing/invoice-finished/${params.invoiceId}`
}

export function invoicePaymentCancelUrl(params: {
  provetId: string
  invoiceId: string
  paymentId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/billing/invoicepayment_cancel/${params.invoiceId}/nfe_payment/${params.paymentId}`,
  })
}

export function creditNoteWizardUrl(params: {
  provetId: string
  invoiceId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/billing/credit_note/${params.invoiceId}/nfe/wizard/?layout=iframe`,
  })
}

export function markAsSentUrl(params: { provetId: string; invoiceId: string }) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/billing/invoice/${params.invoiceId}/nfe/event_log/add`,
  })
}

export function sendInvoiceSummaryEmail(params: {
  provetId: string
  invoiceId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `/billing/invoice/${params.invoiceId}/nfe/summary_email`,
  })
}

export function sendAsEmailTextTemplateUrl(params: { provetId: string }) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_text_templates/target/all?template_types=communications`,
  })
}

export function estimatesUrl(provetId: string) {
  return buildLegacyProvetUrl({ provetId, path: 'estimate/' })
}

export function estimateDetailsUrl(params: {
  provetId: string
  estimateId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `estimate/id/${params.estimateId}`,
  })
}
export function laboratoryReferralDetailsUrl(params: {
  provetId: string
  referralId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `laboratory/referrals/${params.referralId}`,
  })
}

export const externalReferralFinalizationUrl = (params: {
  provetId: string
  referralId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `laboratory/referrals/${params.referralId}/nfe_finalization/`,
  })

export function laboratoryReferralPrintEmailUrl(params: {
  provetId: string
  patientId: string
  referralId: string
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `patient/${params.patientId}/referral/modal/iframe/`,
  })
  url.searchParams.set('select_filter', params.referralId)
  return url
}

export function diagnosticImagingReferralDetailsUrl(params: {
  provetId: string
  referralId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `diagnostic_imaging/referrals/${params.referralId}`,
  })
}

export const diagnosticImagingWorklistCreateUrl = (params: {
  provetId: string
  consultationId: string
}): URL => {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_diagnostic_imaging_worklist_create/${params.consultationId}/`,
  })
}

export const diagnosticImagingWorkflowWorklistCreateUrl = (params: {
  provetId: string
  consultationId: string
}): URL => {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_diagnostic_imaging_workflow_worklist_create/${params.consultationId}/`,
  })
}

export const diagnosticImagingPlanSelectUrl = (params: {
  provetId: string
  consultationId: string
}): URL => {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `consultation/${params.consultationId}/nfe_diagnostic_imaging_plan/modal/`,
  })
}

export const diagnosticImagingWorklistEditUrl = (params: {
  provetId: string
  worklistId: string
}): URL => {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_diagnostic_imaging_worklist_edit/${params.worklistId}/`,
  })
}

export const diagnosticImagingWorkflowWorklistEditUrl = (params: {
  provetId: string
  worklistId: string
}): URL => {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `nfe_diagnostic_imaging_workflow_worklist_edit/${params.worklistId}/`,
  })
}

export function diagnosticImagingReferralPrintUrl(params: {
  provetId: string
  referralId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `diagnostic_imaging/referrals/${params.referralId}/report/modal/`,
  })
}
export function digitalPracticeChatWidgetUrl(
  params: DigitalPracticeChatWidgetUrlParams,
) {
  const { baseUrl, path = '', query } = params

  if (!baseUrl || typeof baseUrl !== 'string' || !baseUrl.trim()) {
    return ''
  }

  let url: URL

  try {
    // Ensure baseUrl always ends with a single slash
    const sanitizedBaseUrl = baseUrl.endsWith('/')
      ? baseUrl.slice(0, -1)
      : baseUrl

    const sanitizedPath = path.startsWith('/') ? path : `/${path}`

    url = new URL(`${sanitizedBaseUrl}${sanitizedPath}`)
  } catch {
    return ''
  }

  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.trim() !== '') {
        url.searchParams.set(key, String(value))
      }
    })
  }

  return url.toString()
}

export function itemModalUrl(params: { provetId: string; itemId: string }) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `organization/show_item_info_modal/${params.itemId}?fullpage`,
  })
}

export function bundleItemModalUrl(params: {
  provetId: string
  bundleId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `item_template/${params.bundleId}?fullpage`,
  })
}

export function clientTagUrl(params: { provetId: string; clientId: string }) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `tag/nfe/client_id/${params.clientId}/iframe/?layout=iframe`,
  })
}

export function patientTagUrl(params: { provetId: string; patientId: string }) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `tag/nfe/patient_id/${params.patientId}/iframe/?layout=iframe`,
  })
}

export function invoiceTagUrl(params: { provetId: string; invoiceId: string }) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `tag/nfe/invoice_id/${params.invoiceId}/iframe/?layout=iframe`,
  })
}

export function sendSmsToPhoneUrl(params: {
  provetId: string
  phoneId: string
  message?: string
  templateContext?: TemplateContext
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `send_new_sms_client_iframe/${params.phoneId}/`,
  })

  if (params.message) {
    url.searchParams.set('message', params.message)
  }

  // Add template context as query parameters
  if (params.templateContext) {
    Object.entries(params.templateContext).forEach(([type, id]) => {
      if (id) {
        url.searchParams.set(type, id)
      }
    })
  }

  return url
}

export function sendSmsToClientUrl(params: {
  provetId: string
  clientId: string
  message?: string
  templateContext?: TemplateContext
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `send_new_simple_sms_client_iframe/${params.clientId}/`,
  })

  if (params.message) {
    url.searchParams.set('message', params.message)
  }

  // Add template context as query parameters
  if (params.templateContext) {
    Object.entries(params.templateContext).forEach(([type, id]) => {
      if (id) {
        url.searchParams.set(type, id)
      }
    })
  }

  return url
}

export function sendEmailToClientUrl(params: {
  provetId: string
  clientId: string
  templateContext?: TemplateContext
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `send_new_email_client_iframe/${params.clientId}/`,
  })

  // Add template context as query parameters
  if (params.templateContext) {
    Object.entries(params.templateContext).forEach(([type, id]) => {
      if (id) {
        url.searchParams.set(type, id)
      }
    })
  }

  return url
}

export function usersSettingsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: '/organization/administration/users/',
  })
}

/**
 * Builds a URL pointing to the personnel appointments page in the legacy ProveT Cloud application.
 *
 * @param {string} provetId - The ProveT instance identifier
 * @returns {URL} A URL object pointing to the personnel appointments page
 *
 * @example
 * personnelAppointmentsUrl('33')
 */
export function personnelAppointmentsUrl(provetId: string) {
  return buildLegacyProvetUrl({
    provetId,
    path: '/personnel/appointments/',
  })
}

export const patientReminderCreateUrl = (params: {
  provetId: string
  patientId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `reminder/patient/${params.patientId}/reminders/nfe_create/`,
  })

export const patientReminderEditUrl = (params: {
  provetId: string
  reminderId: string
}): URL =>
  buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `reminder/${params.reminderId}/nfe_update/`,
  })

export function integrationsSettingsUrl(provetId: string, anchor?: string) {
  return buildLegacyProvetUrl({
    provetId,
    path:
      '/organization/administration/integrations/' +
      (anchor ? `#${anchor}` : ''),
  })
}

/* Catalog Item URLs ============================================================================= */

/**
 * Builds a URL for the NFE catalog item create modal.
 * Uses the standalone NFE view that renders correctly in iframes.
 *
 * @param params.provetId - The ProveT instance identifier
 * @param params.itemType - Item type string enum (e.g. MEDICINE, PROCEDURE)
 * @param params.templateItemId - Optional item ID to use as a template for the new item
 */
export function catalogItemCreateUrl(params: {
  provetId: string
  itemType: string
  templateItemId?: string
}) {
  const url = buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `organization/administration/items/nfe/${params.itemType}/new/`,
  })
  if (params.templateItemId) {
    url.searchParams.set('template_item_id', params.templateItemId)
  }
  return url
}

/**
 * Builds a URL for the NFE catalog item edit modal.
 * Uses the standalone NFE view that renders correctly in iframes.
 *
 * @param params.provetId - The ProveT instance identifier
 * @param params.itemId - The catalog item ID to edit
 */
export function catalogItemEditUrl(params: {
  provetId: string
  itemId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `organization/administration/items/nfe/${params.itemId}/edit/`,
  })
}

/**
 * Builds a URL for the NFE catalog item log view.
 * Uses the standalone NFE view that renders correctly in iframes.
 *
 * @param params.provetId - The ProveT instance identifier
 * @param params.itemId - The catalog item ID
 */
export function catalogItemLogUrl(params: {
  provetId: string
  itemId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `organization/administration/items/nfe/${params.itemId}/log/`,
  })
}

/**
 * Builds a URL for the catalog item convert modal.
 *
 * @param params.provetId - The ProveT instance identifier
 * @param params.itemId - The catalog item ID to convert
 */
export function catalogItemConvertUrl(params: {
  provetId: string
  itemId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `organization/administration/items/nfe/${params.itemId}/convert/`,
  })
}

/**
 * Builds a URL for the NFE stock item view modal.
 * Uses the standalone NFE view that renders correctly in iframes.
 *
 * @param params.provetId - The ProveT instance identifier
 * @param params.departmentId - The department ID
 * @param params.itemId - The organization item ID
 */
export function catalogItemStockUrl(params: {
  provetId: string
  departmentId: string
  itemId: string
}) {
  return buildLegacyProvetUrl({
    provetId: params.provetId,
    path: `stock/department/${params.departmentId}/nfe/org_item/${params.itemId}/`,
  })
}
